import { Box, CircularProgress, FormHelperText, Link } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import React, { useCallback, useMemo } from 'react';
import { MdDelete, MdOutlineUploadFile } from 'react-icons/md';
import { useToastValue } from '../../../../contexts/toastMessage';
import { sendFile } from '../../../../services/form-flow';
import { validateAndReturnResponse, handleApiError } from '../../../../utils/apiValidation';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#264259',
      },
      display: 'block',
    },
  },
  input: {
    fontSize: '16px',
    padding: '2px',
    width: '100%',
    maxWidth: '150px',
  },
  inputError: {
    fontSize: '16px',
    padding: '2px',
    width: '100%',
    maxWidth: '150px',
    border: '2px solid #f44336',
    borderRadius: '4px',
  },
  label: {
    display: 'block',
    marginBottom: theme.spacing(1),
    marginTop: 10,
  },
  boxFile: {
    display: 'flex',
    border: '1px solid #e0e0e0',
    justifyContent: 'space-between',
    padding: '10px',
    borderRadius: '10px',
  },
  button: {
    border: 'none',
    background: 'transparent',
    color: '#264259',
  },
  boxButton: {
    borderLeft: '1px solid #e0e0e0',
    paddingLeft: '10px',
  },
  documentIcon: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  document: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `1px solid #E1E6EF`,
    padding: 0,
    marginTop: 7,
    borderRadius: 4,
    width: '100%',
  },
  documentName: {
    fontSize: theme.typography.pxToRem(16),
    fontStyle: 'normal',
    fontWeight: 400,
    paddingLeft: 20,
    display: 'flex',
    alignItems: 'center',
    color: '#333',
    padding: '10px 0px',
  },
  removeDoc: {
    border: 'none',
    backgroundColor: 'transparent',
    width: 50,
    cursor: 'pointer',
  },
  removeIcon: {
    width: 20,
    height: 20,
    color: '#f00',
  },
  main: {
    width: '100%',
    height: '100%',
    minHeight: 96,
    border: '1px solid #E1E6EF',
    borderRadius: 4,
    padding: 16,
  },
  containerInfoActions: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  containerInfo: {
    width: '100%',
  },
  containerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    gap: 16,
  },
  containerFileList: {
    width: '100%',
  },
  legendText: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 400,
    color: '#55608099',
    display: 'block',
    marginBottom: 4,
  },
  buttonShow: {
    width: 150,
    height: 32,
    borderRadius: 4,
    border: '1px solid #55608029',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    color: '#556080',
    cursor: 'pointer',
    backgroundColor: '#55608029',
  },
  buttonShowDisabled: {
    width: 150,
    height: 32,
    borderRadius: 4,
    border: '1px solid #55608029',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    color: '#556080',
    backgroundColor: '#1f222b29',
  },
  boxButtonFile: {
    width: 150,
    height: 32,
    borderRadius: 4,
    border: '1px solid #55608029',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    color: '#556080',
    backgroundColor: '#55608029',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  boxButtonFileDisabled: {
    backgroundColor: '#1f222b29',
    width: 150,
    height: 32,
    borderRadius: 4,
    border: '1px solid #55608029',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    color: '#556080',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  containerButtonFile: {
    position: 'relative',
    overflow: 'hidden',
    display: 'inline-block',
    '& input[type=file]': {
      position: 'absolute',
      left: 0,
      top: 0,
      opacity: 0,
    },
  },
  container: {
    marginBottom: theme.spacing(1),
  },
}));

type FileInfo = {
  url: string;
  nome: string;
  id: string;
};

type Props = {
  required?: boolean;
  id: string;
  label: string;
  value:
  | ''
  | {
    url: string;
    nome: string;
    id: string;
  }[];
  error?: boolean;
  onChange: (
    value:
      | ''
      | {
        url: string;
        nome: string;
        id: string;
      }[],
  ) => void;
  helperText?: string;
  disabled?: boolean;
  numberFilesPermitted?: number;
  tipos_arquivos?: string[];
  tamanho_maximo_permitido?: number;
};

export default function MultArquivo(props: Props): JSX.Element {
  const classes = useStyles();
  const { setToast } = useToastValue();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [file, setFile] = React.useState<FileInfo[] | null>(null);
  const [isShowFile, setIsShowFile] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  React.useEffect(() => {
    if (props.value && Array.isArray(props.value) && props.value.length > 0) {
      setFile(prevFile => {
        const prevFileIds = prevFile?.map(f => f.id).sort().join(',') || '';
        const propsValueIds = (props.value as FileInfo[]).map(f => f.id).sort().join(',');

        if (prevFileIds !== propsValueIds) {
          if (props.value.length > 0) {
            setIsShowFile(true);
          }
          return props.value as FileInfo[];
        }
        return prevFile;
      });
    } else if (!props.value || (typeof props.value === 'string' && props.value === '') || (Array.isArray(props.value) && props.value.length === 0)) {
      setFile(prevFile => {
        if (prevFile && prevFile.length > 0) {
          setIsShowFile(false);
          return null;
        }
        return prevFile;
      });
    }
  }, [props.value]);

  const getAcceptAttribute = (): string => {
    if (!props.tipos_arquivos || props.tipos_arquivos.length === 0) {
      return '';
    }
    return props.tipos_arquivos.map(ext => `.${ext.toLowerCase()}`).join(',');
  };

  const validateFileExtension = (fileName: string): boolean => {
    if (!props.tipos_arquivos || props.tipos_arquivos.length === 0) {
      return true;
    }
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    return props.tipos_arquivos.some(ext => ext.toLowerCase() === extension);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const list = [];
      setLoading(true);
      setErrorMessage('');
      const listFiles: any = event.target.files;
      const currentFileCount = file?.length || 0;
      const totalFilesAfterUpload = currentFileCount + (listFiles?.length || 0);

      if (
        listFiles?.length > 0 &&
        totalFilesAfterUpload <= props?.numberFilesPermitted
      ) {
        const arquivosInvalidos: string[] = [];
        const arquivosGrandes: string[] = [];

        for (const fileUpload of listFiles) {
          if (!validateFileExtension(fileUpload.name)) {
            arquivosInvalidos.push(fileUpload.name);
          }
          if (props.tamanho_maximo_permitido && fileUpload.size > props.tamanho_maximo_permitido) {
            arquivosGrandes.push(fileUpload.name);
          }
        }

        if (arquivosInvalidos.length > 0) {
          const tiposPermitidos = props.tipos_arquivos?.map(ext => `.${ext.toLowerCase()}`).join(', ') || '';
          const errorMsg = `Extensão não permitida. Tipos permitidos: ${tiposPermitidos}`;
          setErrorMessage(errorMsg);
          setToast({
            visible: true,
            type: 'error',
            message: errorMsg,
          });
          event.target.value = '';
          setLoading(false);
          return;
        }

        if (arquivosGrandes.length > 0) {
          const tamanhoMaximo = props.tamanho_maximo_permitido
            ? formatFileSize(props.tamanho_maximo_permitido)
            : 'tamanho máximo';
          const arquivosComTamanho = arquivosGrandes.map(nome => {
            const arquivo = Array.from(listFiles).find((f: any) => f.name === nome) as File;
            const tamanhoArquivo = arquivo ? formatFileSize(arquivo.size) : '';
            return `${nome} (${tamanhoArquivo})`;
          }).join(', ');
          const errorMsg = `Arquivo(s) muito grande(s): ${arquivosComTamanho}. Tamanho máximo permitido: ${tamanhoMaximo}`;
          setErrorMessage(errorMsg);
          setToast({
            visible: true,
            type: 'error',
            message: errorMsg,
          });
          event.target.value = '';
          setLoading(false);
          return;
        }

        for (const fileUpload of listFiles) {
          try {
            const response = await sendFile(fileUpload, false);
            validateAndReturnResponse(response);

            const dataSend = {
              url: response.data.arquivo,
              nome: fileUpload.name,
              id: response.data.id,
            };

            list.push(dataSend);
          } catch (fileError) {
            const fileErrorMessage = handleApiError(fileError as any, true);

            setToast({
              visible: true,
              type: 'error',
              message: `Erro ao enviar arquivo "${fileUpload.name}": ${fileErrorMessage}`,
            });

            setErrorMessage(`Erro ao enviar "${fileUpload.name}": ${fileErrorMessage}`);

            event.target.value = '';
            setLoading(false);
            return;
          }
        }
      } else {
        if (listFiles?.length > 0 && totalFilesAfterUpload > props?.numberFilesPermitted) {
          const remainingSlots = props?.numberFilesPermitted - currentFileCount;
          const errorMsg = `Limite de ${props?.numberFilesPermitted} arquivo(s) atingido. Você tem ${currentFileCount} arquivo(s) e pode adicionar apenas ${remainingSlots} mais.`;
          setErrorMessage(errorMsg);
          setToast({
            visible: true,
            type: 'error',
            message: errorMsg,
          });
          event.target.value = '';
          setLoading(false);
          return;
        }
        return '';
      }

      if (
        file &&
        file.length > 0 &&
        file.length + list?.length <= props.numberFilesPermitted
      ) {
        setFile([...file, ...list]);
        props.onChange([...file, ...list]);
      } else {
        setFile(list);
        props.onChange(list);
      }

      setErrorMessage('');
      setIsShowFile(true);
    } catch (error) {
      const errorMessage = handleApiError(error as any, true);
      setErrorMessage(errorMessage);

      setToast({
        visible: true,
        type: 'error',
        message: `Erro ao enviar arquivo(s): ${errorMessage}`,
      });

      event.target.value = '';
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (id: string) => {
    setLoading(true);
    const currentFiles: FileInfo[] = file || (Array.isArray(props.value) ? props.value : []);
    const filterFile = currentFiles.filter(oneFile => oneFile.id !== id);
    props.onChange(filterFile.length > 0 ? filterFile : '');
    setFile(filterFile.length > 0 ? filterFile : null);
    setLoading(false);
  };

  const handleSwitchShowFiles = () => setIsShowFile(oldShow => !oldShow);

  const checkIsLimitFileAdded = useMemo(() => {
    const currentFiles = file || (Array.isArray(props.value) ? props.value : []);
    return currentFiles.length === props.numberFilesPermitted;
  }, [file, props.value, props.numberFilesPermitted]);

  const renderTextLabel = useCallback(() => {
    if (props.helperText) {
      return (
        <FormHelperText error={props.error}>{props.helperText}</FormHelperText>
      );
    }
  }, [props]);

  return (
    <Box className={classes.container}>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Box className={classes.main}>
            <Box className={classes.containerInfoActions}>
              <Box className={classes.containerInfo}>
                <p className={classes.label}>
                  {props.label.replace(/(<([^>]+)>)/gi, '')}
                </p>
                <span className={classes.legendText}>
                  Limite de arquivos: {props?.numberFilesPermitted}
                </span>
                <span className={classes.legendText}>
                  Arquivos adicionados: {file?.length || (Array.isArray(props.value) ? props.value.length : 0) || 0}
                </span>
              </Box>

              <Box className={classes.containerActions}>
                <button
                  type='button'
                  className={
                    !(file?.length || (Array.isArray(props.value) && props.value.length > 0))
                      ? classes.boxButtonFileDisabled
                      : classes.buttonShow
                  }
                  onClick={handleSwitchShowFiles}
                  disabled={!(file?.length || (Array.isArray(props.value) && props.value.length > 0))}
                >
                  {isShowFile && (file?.length > 0 || (Array.isArray(props.value) && props.value.length > 0))
                    ? 'Ocultar arquivos'
                    : 'Visualizar arquivos'}
                </button>
                <Box className={classes.containerButtonFile}>
                  <label
                    htmlFor={props.id}
                    className={
                      checkIsLimitFileAdded
                        ? classes.boxButtonFileDisabled
                        : classes.boxButtonFile
                    }
                  >
                    Escolher arquivos
                  </label>
                  <input
                    type='file'
                    id={props.id}
                    className={errorMessage ? classes.inputError : classes.input}
                    name={props.id}
                    onChange={handleChange}
                    disabled={checkIsLimitFileAdded ? true : props.disabled}
                    required={(file?.length > 0 || (Array.isArray(props.value) && props.value.length > 0)) ? false : props.required}
                    multiple
                    title=''
                    accept={getAcceptAttribute()}
                  />
                </Box>
              </Box>
            </Box>
            {isShowFile && (
              <Box className={classes.containerFileList}>
                {(file || (Array.isArray(props.value) && props.value.length > 0)) &&
                  (file || (props.value as FileInfo[])).map((oneFile, index) => (
                    <div className={classes.document} key={oneFile.id || oneFile.nome || index}>
                      <Link
                        className={classes.documentName}
                        href={oneFile.url}
                        target='_blank'
                      >
                        <MdOutlineUploadFile className={classes.documentIcon} />
                        {oneFile.nome}
                      </Link>
                      <button
                        type='button'
                        onClick={(): void => handleRemove(oneFile.id)}
                        className={classes.removeDoc}
                      >
                        <MdDelete className={classes.removeIcon} />
                      </button>
                    </div>
                  ))}
              </Box>
            )}
          </Box>
          {errorMessage && (
            <FormHelperText error={true} style={{ marginTop: 8 }}>
              {errorMessage}
            </FormHelperText>
          )}
          {!errorMessage && renderTextLabel()}
        </>
      )}
    </Box>
  );

}